import React, { useState } from "react";
import { useQueryClient } from "react-query";
import { Button } from "../../common/Button";
import { failed, success } from "../../common/Toastify";
import { useSchedulingSettingsQuery, schedulingSettingsKeys } from "../../../hooks/ReactQueryHooks/useSchedulingSettingsQuery";
import {
  BOOKING_HORIZON_PRESETS,
  DEFAULT_SCHEDULING_SETTINGS,
  HORIZON_SLOT_SEARCH_GAP_DAYS,
  SLOT_SEARCH_PRESETS,
  isSlotSearchAllowed,
  updateSchedulingSettings,
} from "../../api/Scheduling/settings";
import { schedulingErrorMessage } from "../../api/Scheduling/errors";
import "./SchedulingSettings.css";

const daysLabel = (days) => `${days} days`;

function PresetChoice({ label, value, options, onSelect, isDisabled, disabledReason }) {
  return (
    <div className="scheduling-settings-presets" role="radiogroup" aria-label={label}>
      {options.map((option) => {
        const disabled = isDisabled?.(option) || false;
        return (
          <button
            key={option}
            type="button"
            role="radio"
            aria-checked={value === option}
            aria-disabled={disabled || undefined}
            className={`scheduling-settings-preset ${value === option ? "is-selected" : ""}`}
            title={disabled ? disabledReason?.(option) : undefined}
            onClick={() => {
              if (!disabled) onSelect(option);
            }}
          >
            {daysLabel(option)}
          </button>
        );
      })}
    </div>
  );
}

export default function SchedulingSettings({ orgId, active = true }) {
  const queryClient = useQueryClient();
  const { data: settings, isLoading, isError, refetch } = useSchedulingSettingsQuery({
    organizationRef: orgId,
    enabled: !!orgId && active,
  });
  // `edits` is null until the admin changes something, so the form mirrors the
  // saved settings without an effect that could overwrite a fresh click.
  const [edits, setEdits] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const saved = settings
    ? { bookingHorizonDays: settings.bookingHorizonDays, slotSearchDays: settings.slotSearchDays }
    : DEFAULT_SCHEDULING_SETTINGS;
  const draft = edits ?? saved;

  const dirty =
    !!settings &&
    !!edits &&
    (draft.bookingHorizonDays !== saved.bookingHorizonDays || draft.slotSearchDays !== saved.slotSearchDays);
  const slotAllowed = isSlotSearchAllowed(draft.slotSearchDays, draft.bookingHorizonDays);

  const chooseHorizon = (bookingHorizonDays) => {
    setError("");
    // Keep the pair valid: fall back to the widest slot search that still fits.
    const slotSearchDays = isSlotSearchAllowed(draft.slotSearchDays, bookingHorizonDays)
      ? draft.slotSearchDays
      : [...SLOT_SEARCH_PRESETS].reverse().find((option) => isSlotSearchAllowed(option, bookingHorizonDays)) ||
        draft.slotSearchDays;
    setEdits({ bookingHorizonDays, slotSearchDays });
  };

  const save = () => {
    if (!slotAllowed) {
      setError(`Slot searches must end at least ${HORIZON_SLOT_SEARCH_GAP_DAYS} days before the booking horizon.`);
      return;
    }
    setSaving(true);
    setError("");
    updateSchedulingSettings({
      organizationRef: orgId,
      bookingHorizonDays: draft.bookingHorizonDays,
      slotSearchDays: draft.slotSearchDays,
      expectedVersion: settings?.version ?? 0,
    })
      .then((res) => {
        const savedSettings = res?.settings || res;
        queryClient.setQueryData(schedulingSettingsKeys.one(orgId), savedSettings);
        setEdits(null);
        const pending = res?.horizonExtension?.pending;
        success(
          pending
            ? "Scheduling settings saved. Calendars are being extended and will finish overnight."
            : "Scheduling settings saved."
        );
      })
      .catch((requestError) => {
        const message = schedulingErrorMessage(requestError);
        setError(message);
        failed(message);
      })
      .finally(() => setSaving(false));
  };

  if (!orgId) return null;

  return (
    <section className="scheduling-settings" aria-labelledby="scheduling-settings-title">
      <header className="scheduling-settings-header">
        <div>
          <h3 id="scheduling-settings-title">Scheduling</h3>
          <p>How far ahead this organization opens its calendars and how wide a single search can be.</p>
        </div>
        {settings?.isDefault ? <span className="scheduling-settings-badge">Using defaults</span> : null}
      </header>

      {isLoading ? <p role="status" className="scheduling-settings-status">Loading scheduling settings…</p> : null}
      {isError ? (
        <div className="scheduling-settings-status" role="alert">
          Scheduling settings could not be loaded.
          <button type="button" className="scheduling-settings-retry" onClick={() => refetch()}>
            Try again
          </button>
        </div>
      ) : null}

      {!isLoading && !isError ? (
        <>
          <div className="scheduling-settings-row">
            <div className="scheduling-settings-copy">
              <h4>Booking horizon</h4>
              <p>
                Patients can book up to <strong>{daysLabel(draft.bookingHorizonDays)}</strong> ahead. Working hours are
                built out this far and extended every night, so practitioners never have to re-enter a schedule when a
                year ends.
              </p>
            </div>
            <PresetChoice
              label="Booking horizon"
              value={draft.bookingHorizonDays}
              options={BOOKING_HORIZON_PRESETS}
              onSelect={chooseHorizon}
            />
          </div>

          <div className="scheduling-settings-row">
            <div className="scheduling-settings-copy">
              <h4>Slot search window</h4>
              <p>
                One availability search can cover up to <strong>{daysLabel(draft.slotSearchDays)}</strong>. It must end
                at least {HORIZON_SLOT_SEARCH_GAP_DAYS} days before the booking horizon so a search never runs past the
                built calendar.
              </p>
            </div>
            <PresetChoice
              label="Slot search window"
              value={draft.slotSearchDays}
              options={SLOT_SEARCH_PRESETS}
              onSelect={(slotSearchDays) => {
                setError("");
                setEdits({ ...draft, slotSearchDays });
              }}
              isDisabled={(option) => !isSlotSearchAllowed(option, draft.bookingHorizonDays)}
              disabledReason={(option) =>
                `${daysLabel(option)} is too close to a ${daysLabel(draft.bookingHorizonDays)} horizon. Raise the horizon or choose a shorter search window.`
              }
            />
          </div>

          {error ? (
            <p className="scheduling-settings-error" role="alert">
              {error}
            </p>
          ) : null}

          <footer className="scheduling-settings-actions">
            <span className="scheduling-settings-summary">
              {dirty ? "Unsaved changes" : settings?.updatedAt ? `Last saved ${new Date(settings.updatedAt).toLocaleDateString()}` : "Defaults in use"}
            </span>
            <Button
              variant="secondary"
              disabled={!dirty || saving}
              onClick={() => {
                setEdits(null);
                setError("");
              }}
            >
              Reset
            </Button>
            <Button type="button" onClick={save} isLoading={saving} disabled={!dirty || !slotAllowed}>
              Save scheduling settings
            </Button>
          </footer>
        </>
      ) : null}
    </section>
  );
}
